import * as React from "react";
import { Color, SizeOption } from "../../utils/theme";

import { cx } from "../../utils/dom";
import { AsProps, ResponsiveProp } from "../../utils/types";
import { breakpointClassNames } from "../../utils/helpers";

type TextWeight =
  | "light"
  | "regular"
  | "medium"
  | "semi-bold"
  | "bold"
  | "heavy"
  | "inherit";
type TextColorVariant = "light" | "lighter" | "dark" | "default";

interface TextProps {
  size?: ResponsiveProp<SizeOption>;
  weight?: TextWeight;
  highlight?: boolean;
  color?: Color | "inherit" | "white" | "black";
  variant?: TextColorVariant;
  align?: "left" | "center" | "right";
  numeric?: boolean;
  underline?: boolean;
  italic?: boolean;
  lineHeight?: "normal" | "shorter" | "short" | "base" | "tall";
  textCase?: "uppercase" | "lowercase" | "capitalize";
}

export const _Text = <T extends React.ElementType = "span">(
  props: React.PropsWithChildren<TextProps> & AsProps<T>,
  ref?: React.Ref<T>
) => {
  const {
    textCase,
    size = "sm",
    weight,
    color = "gray",
    variant = "default",
    highlight,
    align,
    lineHeight,
    underline,
    italic,
    as: Comp = "span",
    className,
    ...rest
  } = props;

  return (
    <Comp
      className={cx(
        "p-text",
        className,
        {
          [`text-highlight`]: !!highlight,
          [`text-weight-${weight}`]: !!weight,
          [`text-align-${align}`]: !!align,
          [`text-line-height-${lineHeight}`]: !!lineHeight,
          [`text-transform-${textCase}`]: !!textCase,
        },
        breakpointClassNames("text-size", size)
      )}
      data-underline={underline}
      data-italic={italic}
      data-color={color}
      data-color-variant={variant}
      ref={ref}
      {...rest}
    />
  );
};

_Text.displayName = "Text";

export const Text = React.forwardRef(_Text) as typeof _Text;
